import React, { useState, useRef, useEffect } from 'react';
import './QuestVideoCapture.css';
import QUEST_FEATURES from '../../config/questFeatures';
import Tesseract from 'tesseract.js';
import { analyzePackageLabel } from '../../services/visionService';

/**
 * Quest Video Capture Component
 * Kontinuierlicher Video-Stream mit OCR und Vision API
 * 📸 NEU: Unterstützt Screenshot-Modus für Passthrough!
 */
const QuestVideoCapture = ({ onDataScanned, isActive, useScreenshot = false }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const ocrIntervalRef = useRef(null);
  const workerRef = useRef(null);
  
  const [isScanning, setIsScanning] = useState(false);
  const [previewText, setPreviewText] = useState('');
  const [cameraActive, setCameraActive] = useState(false);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [confidence, setConfidence] = useState(0);
  const [lastOcrTime, setLastOcrTime] = useState(0);
  const [countdown, setCountdown] = useState(null);
  const [dataScanned, setDataScanned] = useState(false); // ✅ Trackt ob Daten erfolgreich gescannt wurden
  const [visionError, setVisionError] = useState(null); // ⚠️ Vision API Fehler
  const countdownIntervalRef = useRef(null);
  
  // ✅ System-Info States
  const [systemInfo, setSystemInfo] = useState({
    fps: 0,
    brightness: 0,
    memory: 0,
    battery: null,
    connection: 'unknown'
  });
  const fpsCounterRef = useRef({ frames: 0, lastTime: Date.now() });

  // ✅ System-Info Monitoring
  useEffect(() => {
    if (!cameraActive) return;

    const updateSystemInfo = async () => {
      const info = {};

      // 🔋 Batterie
      if ('getBattery' in navigator) {
        try {
          const battery = await navigator.getBattery();
          info.battery = {
            level: Math.round(battery.level * 100),
            charging: battery.charging
          };
        } catch (e) {
          info.battery = null;
        }
      }

      // 🧠 RAM (nur in Chrome/Edge)
      if (performance.memory) {
        const used = performance.memory.usedJSHeapSize / 1048576;
        const total = performance.memory.jsHeapSizeLimit / 1048576;
        info.memory = Math.round((used / total) * 100);
      }

      // 📶 Netzwerk
      if ('connection' in navigator || 'mozConnection' in navigator || 'webkitConnection' in navigator) {
        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        info.connection = connection.effectiveType || 'unknown';
      }

      // 💡 Helligkeit (aus Video-Frame)
      if (videoRef.current && canvasRef.current) {
        try {
          const canvas = canvasRef.current;
          const ctx = canvas.getContext('2d');
          const video = videoRef.current;
          
          if (video.videoWidth > 0) {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;
            let brightness = 0;
            
            for (let i = 0; i < data.length; i += 40) {
              brightness += (data[i] + data[i + 1] + data[i + 2]) / 3;
            }
            
            brightness = brightness / (data.length / 40);
            info.brightness = Math.round((brightness / 255) * 100);
          }
        } catch (e) {
          info.brightness = 0;
        }
      }

      setSystemInfo(prev => ({ ...prev, ...info }));
    };

    // 🎥 FPS Counter
    const updateFPS = () => {
      const now = Date.now();
      fpsCounterRef.current.frames++;
      
      if (now - fpsCounterRef.current.lastTime >= 1000) {
        setSystemInfo(prev => ({ 
          ...prev, 
          fps: fpsCounterRef.current.frames 
        }));
        fpsCounterRef.current.frames = 0;
        fpsCounterRef.current.lastTime = now;
      }
      
      if (cameraActive) {
        requestAnimationFrame(updateFPS);
      }
    };

    updateSystemInfo();
    const interval = setInterval(updateSystemInfo, 2000);
    requestAnimationFrame(updateFPS);

    return () => clearInterval(interval);
  }, [cameraActive]);

  useEffect(() => {
    if (!QUEST_FEATURES.enabled || !QUEST_FEATURES.videoMode.enabled) {
      return;
    }

    if (isActive) {
      // Warte kurz, bis Video-Element gemountet ist
      const timer = setTimeout(() => {
        console.log('[Quest Video] Video Ref nach Delay:', !!videoRef.current);
        if (videoRef.current) {
          startCamera();
        } else {
          console.error('[Quest Video] ❌ Video Ref immer noch null nach 300ms!');
          // Nochmal versuchen
          setTimeout(() => {
            if (videoRef.current) {
              console.log('[Quest Video] Retry erfolgreich!');
              startCamera();
            } else {
              console.error('[Quest Video] ❌ Retry fehlgeschlagen - Video Element existiert nicht!');
            }
          }, 500);
        }
      }, 300);
      
      return () => clearTimeout(timer);
    } else {
      stopCamera();
    }

    return () => {
      stopCamera();
      stopOCR();
    };
  }, [isActive]);

  // Initialisiere Tesseract Worker
  useEffect(() => {
    const initWorker = async () => {
      if (!workerRef.current && QUEST_FEATURES.videoMode.continuousOCR) {
        try {
          console.log('[Quest Video] Initialisiere Tesseract Worker...');
          const worker = await Tesseract.createWorker('deu', 1, {
            logger: (m) => {
              if (m.status === 'recognizing text') {
                setOcrProgress(Math.round(m.progress * 100));
              }
            }
          });
          workerRef.current = worker;
          console.log('[Quest Video] Tesseract Worker bereit');
        } catch (error) {
          console.error('[Quest Video] Tesseract Init Error:', error);
        }
      }
    };

    initWorker();

    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
      // Countdown cleanup
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  const startCamera = async () => {
    try {
      console.log('[Quest Video] 🎬 startCamera() aufgerufen');
      console.log('[Quest Video] useScreenshot prop:', useScreenshot);
      
      // 🎯 PASSTHROUGH MODE: Keine Kamera nötig!
      // UI zeigt sich transparent über Passthrough
      // Screenshots werden on-demand gemacht
      
      console.log('[Quest Video] 🎬 Passthrough-Modus aktiviert!');
      console.log('[Quest Video] ✅ Keine Kamera nötig - Passthrough scheint durch UI!');
      
      const isWolvic = navigator.userAgent.includes('Wolvic');
      const isQuestBrowser = navigator.userAgent.includes('OculusBrowser');
      console.log('[Quest Video] Browser:', isWolvic ? 'Wolvic' : (isQuestBrowser ? 'Quest Browser' : 'Unbekannt'));
      
      // 🎯 Passthrough-Modus: UI ist sofort bereit, keine Kamera nötig!
      setCameraActive(true);
      console.log('[Quest Video] ✅ cameraActive auf TRUE gesetzt!');
      console.log('[Quest Video] ✅ UI bereit für Screenshot-Capture!');
      
    } catch (error) {
      console.error('[Quest Video] ❌ Kamera-Fehler:', error);
      console.error('[Quest Video] Error Name:', error.name);
      console.error('[Quest Video] Error Message:', error.message);
      console.error('[Quest Video] Full Error:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
      
      // Detaillierte Fehlermeldung
      let errorDetails = `❌ Kamera-Zugriff fehlgeschlagen!\n\n`;
      errorDetails += `Browser: ${navigator.userAgent.includes('Wolvic') ? 'Wolvic' : 'Quest Browser'}\n\n`;
      errorDetails += `Fehler: ${error.name}\n`;
      errorDetails += `Details: ${error.message}\n\n`;
      errorDetails += `Mögliche Ursachen:\n`;
      errorDetails += `• Kamera-Berechtigung nicht erteilt\n`;
      errorDetails += `• Andere App nutzt die Kamera\n`;
      errorDetails += `• Wolvic-spezifische Einschränkungen\n\n`;
      errorDetails += `Im Quest Browser funktioniert es → Wolvic-Problem`;
      
      alert(errorDetails);
      setCameraActive(false);
    }
  };

  const stopCamera = () => {
    console.log('[Quest Video] Stoppe Kamera...');
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setCameraActive(false);
  };

  const startOCR = () => {
    if (ocrIntervalRef.current) return; // Bereits gestartet

    console.log('[Quest Video] Starte kontinuierliches OCR...');
    
    const interval = QUEST_FEATURES.videoMode.ocrInterval || 500;
    
    ocrIntervalRef.current = setInterval(async () => {
      await performOCR();
    }, interval);
  };

  const stopOCR = () => {
    if (ocrIntervalRef.current) {
      clearInterval(ocrIntervalRef.current);
      ocrIntervalRef.current = null;
      console.log('[Quest Video] OCR gestoppt');
    }
  };

  const performOCR = async () => {
    if (!videoRef.current || !canvasRef.current || !workerRef.current || isScanning) {
      return;
    }

    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      // Prüfen, ob Video bereit ist
      if (!video.videoWidth || !video.videoHeight || video.videoWidth < 100) {
        console.log('[Quest Video] Video noch nicht bereit für OCR (Größe:', video.videoWidth, 'x', video.videoHeight, ')');
        return;
      }

      // Canvas-Größe anpassen
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Aktuelles Frame zeichnen
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      // 🔥 NEUE VALIDIERUNG: Prüfe auf schwarzes/dunkles Bild
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const pixels = imageData.data;
      let totalBrightness = 0;
      
      // Sample nur jeden 10. Pixel für Performance
      for (let i = 0; i < pixels.length; i += 40) { // RGBA = 4 Werte, jeden 10. Pixel
        const r = pixels[i];
        const g = pixels[i + 1];
        const b = pixels[i + 2];
        totalBrightness += (r + g + b) / 3;
      }
      
      const avgBrightness = totalBrightness / (pixels.length / 40);
      
      // Wenn Bild zu dunkel (< 30 von 255), überspringe OCR
      if (avgBrightness < 30) {
        setPreviewText('');
        setConfidence(0);
        return;
      }

      // OCR durchführen
      const startTime = Date.now();
      const { data } = await workerRef.current.recognize(canvas);
      const ocrTime = Date.now() - startTime;

      setLastOcrTime(ocrTime);

      // Text extrahieren
      const text = data.text.trim();
      const conf = data.confidence || 0;

      setConfidence(Math.round(conf));

      // Nur Text mit Mindest-Konfidenz anzeigen
      if (text && text.length > 10 && conf > 50) {
        setPreviewText(text.substring(0, 100) + '...');
        
        // Debug Log
        if (QUEST_FEATURES.debug.enabled) {
          console.log('[Quest OCR]', {
            text: text.substring(0, 50),
            confidence: conf,
            brightness: Math.round(avgBrightness),
            time: ocrTime + 'ms'
          });
        }
      } else {
        // Zu niedrige Konfidenz oder zu kurz
        setPreviewText('');
      }

    } catch (error) {
      console.error('[Quest Video] OCR Error:', error);
    }
  };

  const triggerVisionScan = async () => {
    if (isScanning || !canvasRef.current) {
      return;
    }

    setIsScanning(true);
    setDataScanned(false); // ✅ Reset für neuen Scan
    
    // Ladebalken-Fortschritt starten (geschätzt ~6.5s)
    setCountdown(0); // 0 = Start, wird als Prozent interpretiert
    const loadStartTime = Date.now();
    const estimatedMs = 6500;
    countdownIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - loadStartTime;
      const linearProgress = Math.min(elapsed / estimatedMs, 0.95);
      // Ease-out: Langsamer am Ende
      const progress = Math.round((1 - Math.pow(1 - linearProgress, 2)) * 100);
      setCountdown(progress);
    }, 100);

    // 📸 SCREENSHOT MODE: Passthrough capturen mit getDisplayMedia
    (async () => {
      try {
        console.log('[Quest Screenshot] 📸 Mache Screenshot...');
        
        // 🎯 REQUEST PASSTHROUGH SCREENSHOT via postMessage
        // VRPackagesVanilla.js hat Zugriff auf WebGL Framebuffer!
        console.log('[Quest Screenshot] 📡 Sende Screenshot-Request an VR-Scene...');
        
        // Event Listener für Screenshot-Daten
        const screenshotPromise = new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Screenshot Timeout nach 5 Sekunden'));
          }, 5000);
          
          const handler = (event) => {
            if (event.data && event.data.type === 'VR_SCREENSHOT') {
              clearTimeout(timeout);
              window.removeEventListener('message', handler);
              console.log('[Quest Screenshot] ✅ Screenshot-Daten empfangen!');
              resolve(event.data);
            }
          };
          
          window.addEventListener('message', handler);
        });
        
        // Request Screenshot von VR Scene
        window.postMessage({ type: 'REQUEST_VR_SCREENSHOT' }, '*');
        
        console.log('[Quest Screenshot] ⏳ Warte auf Screenshot-Daten...');
        const screenshotData = await screenshotPromise;
        console.log('[Quest Screenshot] ✅ Screenshot-Daten empfangen:', screenshotData.width, 'x', screenshotData.height);
        
        // 📐 SKALIERUNG: VR Screenshot ist riesig (2560x1440+), wir brauchen nur 640x480
        const targetWidth = 640;
        const targetHeight = 480;
        
        console.log('[Quest Screenshot] Original Größe:', screenshotData.width, 'x', screenshotData.height);
        
        // Temporäres Canvas für Original-Screenshot
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = screenshotData.width;
        tempCanvas.height = screenshotData.height;
        const tempCtx = tempCanvas.getContext('2d');
        
        // ImageData von VR Scene auf temp Canvas
        const originalData = new ImageData(
          new Uint8ClampedArray(screenshotData.pixels),
          screenshotData.width,
          screenshotData.height
        );
        tempCtx.putImageData(originalData, 0, 0);
        
        // Finales Canvas mit Zielgröße
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        
        // 🎯 Skalierung mit drawImage (Browser macht automatisch bilinear filtering)
        ctx.drawImage(tempCanvas, 0, 0, screenshotData.width, screenshotData.height, 0, 0, targetWidth, targetHeight);
        
        console.log('[Quest Screenshot] ✅ Skaliert auf:', targetWidth, 'x', targetHeight);

        // 🎨 BILDOPTIMIERUNG für bessere OCR
        console.log('[Quest Video] 🎨 Optimiere Bild...');
        
        // 1. Kontrast und Helligkeit erhöhen
        const optimizedData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = optimizedData.data;
        
        // Einfache Kontrastverstärkung
        const contrast = 1.3; // 30% mehr Kontrast
        const brightness = 10; // +10 Helligkeit
        
        for (let i = 0; i < data.length; i += 4) {
          // Kontrast: (pixel - 128) * contrast + 128
          data[i] = Math.min(255, Math.max(0, (data[i] - 128) * contrast + 128 + brightness));     // R
          data[i + 1] = Math.min(255, Math.max(0, (data[i + 1] - 128) * contrast + 128 + brightness)); // G
          data[i + 2] = Math.min(255, Math.max(0, (data[i + 2] - 128) * contrast + 128 + brightness)); // B
        }
        
        ctx.putImageData(optimizedData, 0, 0);
        console.log('[Quest Video] ✅ Kontrast erhöht');

        // 🖼️ DEBUG: Screenshot als Data-URL ausgeben (MEHRERE METHODEN!)
        const dataUrl = canvas.toDataURL('image/jpeg', 0.90);
        
        // Methode 1: Direkt als String
        console.log('[Quest Video] 🖼️ Screenshot Preview (640x480):');
        console.log(dataUrl);
        
        // Methode 2: Als anklickbares Image-Element in Console
        const img = new Image();
        img.src = dataUrl;
        img.style.maxWidth = '400px';
        console.log('[Quest Video] 📸 Screenshot Image:', img);
        
        // Methode 3: Split für bessere Sichtbarkeit
        console.log('[Quest Video] 📋 Data-URL Prefix:', dataUrl.substring(0, 100));
        console.log('[Quest Video] 📏 Data-URL Länge:', dataUrl.length, 'Zeichen');

        // Als Blob konvertieren (Quality 0.90 - Balance zwischen Speed und OCR-Qualität)
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.90));
        console.log('[Quest Video] 📸 Bild optimiert, Größe:', Math.round(blob.size / 1024), 'KB');
        console.log('[Quest Video] 🎬 VIDEO LÄUFT WEITER - API Call im Hintergrund...');

        // Vision API aufrufen
        const result = await analyzeWithVisionAPI(blob);

        // Prüfen ob sinnvolle Daten erkannt wurden
        const hasValidData = result && (
          (result.empfaenger && result.empfaenger.trim()) ||
          (result.strasse && result.strasse.trim()) ||
          (result.trackingNummer && result.trackingNummer.trim())
        ) && !result._isMock;

        if (hasValidData && onDataScanned) {
          // ✅ Markiere als gescannt → UI ausblenden
          setDataScanned(true);
          
          // ✅ NICHT-BLOCKIEREND: Callback mit setTimeout verzögern
          // Damit Video-Frame fertig gerendert wird BEVOR State-Update
          setTimeout(() => {
            console.log('[Quest Video] 📤 Callback wird ausgeführt (nicht-blockierend)');
            onDataScanned(result);
          }, 0);

          // Erfolgs-Feedback
          if (navigator.vibrate) {
            navigator.vibrate([100, 50, 100]); // Success pattern
          }
        } else {
          console.warn('[Quest Video] ⚠️ Nichts erkannt oder Daten unbrauchbar:', result);
          setVisionError({
            message: 'Kein Paketlabel erkannt — bitte nochmal versuchen',
            timestamp: Date.now()
          });
          setTimeout(() => setVisionError(null), 4000);

          // Fehler-Vibration
          if (navigator.vibrate) {
            navigator.vibrate(300);
          }
        }

      } catch (error) {
        console.error('[Quest Video] Vision Scan Error:', error);
        alert('Fehler beim Scannen: ' + error.message);
        
        // Error vibration
        if (navigator.vibrate) {
          navigator.vibrate(300);
        }
      } finally {
        setIsScanning(false);
        // Countdown stoppen wenn fertig
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
        }
        setCountdown(null);
      }
    })(); // ✅ IIFE - läuft sofort, aber blockiert nicht
  };

  const analyzeWithVisionAPI = async (imageBlob) => {
    if (!QUEST_FEATURES.visionAPI.enabled) {
      console.warn('[Quest Video] Vision API deaktiviert, verwende Mock-Daten');
      const mockData = getMockData();
      mockData._isMock = true;
      mockData._reason = 'API deaktiviert';
      return mockData;
    }

    try {
      console.log('[Quest Video] 🚀 Starte Vision API Request...');
      
      // Bild zu Base64 konvertieren
      const base64 = await blobToBase64(imageBlob);
      console.log('[Quest Video] ✅ Bild zu Base64 konvertiert:', base64.substring(0, 50) + '...');

      // Vision API Request über VPS (auth via JWT)
      console.log('[Quest Video] 📤 Sende Request an VPS Vision API...');
      const startTime = Date.now();
      
      const data = await analyzePackageLabel(base64, getVisionPrompt());

      const responseTime = Date.now() - startTime;
      console.log('[Quest Video] ⏱️ Response nach', responseTime, 'ms');
      console.log('[Quest Video] ✅ API Response erhalten:', data);

      if (QUEST_FEATURES.debug.logVisionAPI) {
        console.log('[Quest Vision API] Full Response:', JSON.stringify(data, null, 2));
      }

      const parsed = parseVisionResponse(data);
      parsed._isMock = false;
      parsed._apiResponseTime = responseTime;
      
      // ✅ Token-Usage für Kosten-Tracking
      if (data.usage) {
        parsed._tokenUsage = {
          promptTokens: data.usage.prompt_tokens || 0,
          completionTokens: data.usage.completion_tokens || 0,
          totalTokens: data.usage.total_tokens || 0,
          // Kosten berechnen (gpt-4o-mini Preise: $0.150/1M input, $0.600/1M output)
          costUSD: (
            (data.usage.prompt_tokens / 1000000) * 0.150 +
            (data.usage.completion_tokens / 1000000) * 0.600
          )
        };
        console.log('[Quest Video] 💰 Kosten:', `$${parsed._tokenUsage.costUSD.toFixed(6)}`);
      }
      
      console.log('[Quest Video] ✅ Daten geparst:', parsed);
      return parsed;

    } catch (error) {
      console.error('[Quest Video] ❌ Vision API Error:', error);
      console.error('[Quest Video] Error Stack:', error.stack);
      
      // ⚠️ Setze Fehler für UI-Feedback
      setVisionError({
        message: error.message || 'Vision API Fehler',
        timestamp: Date.now()
      });
      
      // Auto-Hide nach 5 Sekunden
      setTimeout(() => setVisionError(null), 5000);
      
      // Fallback zu Mock-Daten
      console.warn('[Quest Video] ⚠️ Verwende Mock-Daten als Fallback');
      const mockData = getMockData();
      mockData._isMock = true;
      mockData._reason = error.message;
      return mockData;
    }
  };

  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const getVisionPrompt = () => {
    return `Du bist ein OCR-System für deutsche Paketlabels. Lies NUR was auf dem Label steht!

🚨 ABSOLUTE REGELN - NIEMALS BRECHEN:
1. ERFINDE KEINE Namen, Adressen oder Daten!
2. Bei unlesbarem Text → null zurückgeben
3. Kein "c/o", "unter der", etc. - NUR der Straßenname!
4. Hausnummer MUSS separat stehen (nicht in Straße!)
5. Namen EXAKT übernehmen - NICHT interpretieren oder abkürzen

📋 PFLICHTFELDER (wenn nicht lesbar → confidence = 0.3):
- empfaenger: Der EXAKTE Name/Firma auf dem Label - Buchstabe für Buchstabe!
  ❌ FALSCH: "FIRMAAbodulution" (zusammengewürfelt/erfunden)
  ❌ FALSCH: "FIRMA" + irgendwas Erfundenes
  ✅ RICHTIG: "Max Mustermann" oder "Amazon GmbH" (EXAKT wie auf Label)
  ⚠️ WENN UNLESERLICH: null setzen statt raten!
  
- strasse: NUR der Straßenname
  ❌ FALSCH: "c/o unter der Hauptstraße 123"
  ✅ RICHTIG: "Hauptstraße"
  
- hausnummer: Die Hausnummer SEPARAT (mit Zusätzen wie a, b)
  ❌ FALSCH: null (wenn im Bild sichtbar!)
  ✅ RICHTIG: "123a"

📦 OPTIONALE FELDER:
- plz: 5-stellige Postleitzahl (nur wenn komplett lesbar)
- ort: Stadt/Ort-Name (nur wenn lesbar)
- paketdienst: Logo-Name (DHL/DPD/Hermes/UPS/GLS/Amazon)
- absender: Absender wenn eindeutig
- paketGroesse: NUR wenn GANZES Paket sichtbar
- paketFarbe: Die SICHTBARE Farbe des Pakets/Kartons!
  🎨 IMMER angeben wenn Paket sichtbar!
  📦 "braun" (typisch DHL/DPD Karton), "weiß", "grau", "gelb" (DHL-Paket), "rot", "orange" etc.
  ✅ Auch wenn nur teilweise sichtbar → gib die dominante Farbe an
  ⚠️ NICHT die Farbe des Adress-Labels, sondern des KARTONS!
- trackingNummer: Die SENDUNGSNUMMER / Sendungsnr.
  ✅ Steht UNTER dem Barcode ODER bei "Sendungsnr.: xxxxx"
  ⚠️ NICHT die "Abrechnungsnummer" (Abrg.-Nr.)!
  ⚠️ NICHT die Kundennummer!
  📝 Meist 12-14 Ziffern
- leitcode: Längere Nummer für Postsortierung (oft 14-stellig, neben/unter Tracking)

🎯 CONFIDENCE:
- 0.9-1.0: Alles klar lesbar
- 0.7-0.9: Meiste Felder lesbar
- 0.3-0.7: Viele Felder unklar → setze sie auf null
- 0.0-0.3: Fast nichts lesbar → alle optionalen Felder null

Antworte NUR mit diesem JSON:
{
  "empfaenger": "...",
  "strasse": "...",
  "hausnummer": "...",
  "plz": "..." oder null,
  "ort": "..." oder null,
  "paketdienst": "..." oder null,
  "absender": "..." oder null,
  "gewicht": "..." oder null,
  "paketGroesse": "..." oder null,
  "paketFarbe": "..." oder null,
  "trackingNummer": "..." oder null,
  "leitcode": "..." oder null,
  "confidence": 0.0-1.0
}`;
  };

  const parseVisionResponse = (apiResponse) => {
    try {
      // Je nach API-Provider unterschiedlich
      let parsed;
      
      if (apiResponse.text) {
        // VPS Gemini Format: { text: "json string", usage: {...} }
        let content = apiResponse.text;
        console.log('[Quest Vision] Raw content:', content);
        content = content.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();
        console.log('[Quest Vision] Cleaned content:', content);
        parsed = JSON.parse(content);
      } else if (apiResponse.choices && apiResponse.choices[0]) {
        // OpenAI Format (Legacy)
        let content = apiResponse.choices[0].message.content;
        content = content.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();
        parsed = JSON.parse(content);
      } else if (apiResponse.data) {
        // Direktes JSON
        parsed = apiResponse.data;
      } else {
        parsed = apiResponse;
      }

      // Validierung
      if (!parsed.empfaenger || !parsed.strasse || !parsed.hausnummer) {
        console.warn('[Quest Vision] Pflichtfelder fehlen:', parsed);
      }

      return parsed;

    } catch (error) {
      console.error('[Quest Video] Parse Error:', error);
      console.error('[Quest Video] API Response:', JSON.stringify(apiResponse, null, 2));
      throw new Error('Konnte Vision API Antwort nicht parsen');
    }
  };

  const getMockData = () => {
    const mockPackages = [
      {
        empfaenger: 'Max Mustermann',
        strasse: 'Hauptstraße',
        hausnummer: '123',
        plz: '12345',
        ort: 'Berlin',
        paketdienst: 'DHL',
        absender: 'Amazon',
        paketGroesse: 'mittel',
        confidence: 0.95
      },
      {
        empfaenger: 'Julia Schmidt',
        strasse: 'Goethestraße',
        hausnummer: '45',
        plz: '80331',
        ort: 'München',
        paketdienst: 'DPD',
        absender: 'Zalando',
        confidence: 0.92
      },
      {
        empfaenger: 'Thomas Weber',
        strasse: 'Lindenallee',
        hausnummer: '78',
        plz: '50667',
        ort: 'Köln',
        paketdienst: 'Hermes',
        absender: 'MediaMarkt',
        paketGroesse: 'groß',
        confidence: 0.88
      }
    ];

    return mockPackages[Math.floor(Math.random() * mockPackages.length)];
  };

  const handleManualScan = () => {
    triggerVisionScan();
  };

  // Voice Commands Setup
  useEffect(() => {
    if (!QUEST_FEATURES.triggers.voiceCommand.enabled || !cameraActive) {
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      console.warn('[Quest Video] Speech Recognition nicht verfügbar');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.lang = QUEST_FEATURES.triggers.voiceCommand.language;
    recognition.interimResults = false;

    recognition.onresult = (event) => {
      const last = event.results.length - 1;
      const command = event.results[last][0].transcript.toLowerCase().trim();
      
      console.log('[Quest Voice] Command:', command);

      const commands = QUEST_FEATURES.triggers.voiceCommand.commands;
      
      if (commands.some(cmd => command.includes(cmd))) {
        console.log('[Quest Voice] Trigger erkannt!');
        triggerVisionScan();
      }
    };

    recognition.onerror = (event) => {
      console.error('[Quest Voice] Error:', event.error);
    };

    recognition.start();
    console.log('[Quest Voice] Voice Commands aktiv');

    return () => {
      recognition.stop();
    };
  }, [cameraActive]);

  return (
    <div className="quest-video-capture">
      <div className="video-container" style={{ position: 'relative' }}>
        {/* 🎯 PASSTHROUGH MODE: Kein Video, UI transparent über Passthrough */}
        {/* Video-Element wird NICHT angezeigt - Passthrough scheint durch */}
        <video 
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="video-stream"
          style={{ 
            display: 'none', // ❌ Video versteckt - Passthrough sichtbar!
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
        
        {/* Canvas nur für Snapshot beim Scannen (versteckt) */}
        <canvas 
          ref={canvasRef}
          className="overlay-canvas"
          style={{ display: 'none' }}
        />
        
        {/* Capture Area Frame - AUSBLENDEN wenn gescannt */}
        {cameraActive && !dataScanned && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'min(480px, 80vw)',
            height: 'min(540px, 90vh)',
            border: '3px solid #4CAF50',
            borderRadius: '8px',
            boxShadow: '0 0 20px rgba(76, 175, 80, 0.5), inset 0 0 20px rgba(76, 175, 80, 0.2)',
            pointerEvents: 'none',
            zIndex: 5
          }}>
            {/* Corner markers für bessere Sichtbarkeit */}
            <div style={{
              position: 'absolute',
              top: '-3px',
              left: '-3px',
              width: '40px',
              height: '40px',
              borderTop: '6px solid #4CAF50',
              borderLeft: '6px solid #4CAF50',
              borderRadius: '8px 0 0 0'
            }} />
            <div style={{
              position: 'absolute',
              top: '-3px',
              right: '-3px',
              width: '40px',
              height: '40px',
              borderTop: '6px solid #4CAF50',
              borderRight: '6px solid #4CAF50',
              borderRadius: '0 8px 0 0'
            }} />
            <div style={{
              position: 'absolute',
              bottom: '-3px',
              left: '-3px',
              width: '40px',
              height: '40px',
              borderBottom: '6px solid #4CAF50',
              borderLeft: '6px solid #4CAF50',
              borderRadius: '0 0 0 8px'
            }} />
            <div style={{
              position: 'absolute',
              bottom: '-3px',
              right: '-3px',
              width: '40px',
              height: '40px',
              borderBottom: '6px solid #4CAF50',
              borderRight: '6px solid #4CAF50',
              borderRadius: '0 0 8px 0'
            }} />
            
            {/* Resolution Label */}
            <div style={{
              position: 'absolute',
              top: '8px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(76, 175, 80, 0.9)',
              color: 'white',
              padding: '6px 16px',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: 'bold',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
            }}>
              480×540 Capture
            </div>
          </div>
        )}
        
        {/* Placeholder ODER Scan Button - AUSBLENDEN wenn gescannt */}
        {!cameraActive ? (
          <div className="camera-placeholder">
            <div className="placeholder-icon">📹</div>
            <p>Kamera-Stream</p>
            <p className="placeholder-hint">
              Kamera-Berechtigung erlauben zum Starten
            </p>
          </div>
        ) : !dataScanned ? (
          <div className="scan-overlay" style={{
            position: 'absolute',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10
          }}>
            <button 
              className="scan-trigger-button"
              onClick={handleManualScan}
              disabled={isScanning}
              style={{
                fontSize: '24px',
                padding: '20px 40px',
                background: isScanning ? '#666' : '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                cursor: isScanning ? 'wait' : 'pointer',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                minWidth: '200px',
                fontWeight: 'bold'
              }}
            >
              {isScanning ? '🤖 KI analysiert...' : '📸 SCANNEN'}
            </button>
          </div>
        ) : null}
      </div>

      {/* Status mit Countdown */}
      <div className="capture-status" style={{
        padding: '12px',
        textAlign: 'center',
        fontSize: '16px',
        fontWeight: '500'
      }}>
        {isScanning ? (
          <div>
            <span className="status-scanning" style={{ color: '#2196F3' }}>
              🤖 KI analysiert Paketlabel...
            </span>
            {countdown !== null && (
              <div style={{ marginTop: '8px' }}>
                <div style={{
                  width: '100%',
                  height: '12px',
                  background: 'rgba(255,255,255,0.15)',
                  borderRadius: '6px',
                  overflow: 'hidden',
                  marginBottom: '4px'
                }}>
                  <div style={{
                    width: `${countdown}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #2196F3, #4CAF50)',
                    borderRadius: '6px',
                    transition: 'width 0.1s linear'
                  }} />
                </div>
                <span style={{ fontSize: '14px', color: '#aaa' }}>
                  Paketdaten werden erkannt...
                </span>
              </div>
            )}
          </div>
        ) : cameraActive && !dataScanned ? (
          <span className="status-ready" style={{ color: '#4CAF50' }}>✓ Bereit zum Scannen</span>
        ) : !cameraActive ? (
          <span className="status-waiting" style={{ color: '#666' }}>⏳ Warte auf Kamera...</span>
        ) : null}
      </div>

      {/* ⚠️ Vision API Error Banner */}
      {visionError && (
        <div style={{
          position: 'absolute',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#ff5722',
          color: 'white',
          padding: '12px 24px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(255, 87, 34, 0.4)',
          zIndex: 100,
          fontSize: '14px',
          fontWeight: 'bold',
          maxWidth: '80%',
          textAlign: 'center'
        }}>
          ⚠️ Vision API Fehler: {visionError.message}
          <div style={{ fontSize: '12px', marginTop: '4px', opacity: 0.9 }}>
            Verwende Fallback-Daten
          </div>
        </div>
      )}

      {/* Instructions - AUSBLENDEN wenn gescannt */}
      {cameraActive && !isScanning && !dataScanned && (
        <div className="instructions" style={{
          padding: '16px',
          textAlign: 'center',
          fontSize: '14px',
          color: '#666'
        }}>
          <p style={{ margin: '0 0 8px 0', fontWeight: '500' }}>📦 Halte das Paket-Label vor die Kamera</p>
          <p style={{ margin: 0, fontSize: '13px' }}>Klicke auf "SCANNEN" wenn das Label sichtbar ist</p>
        </div>
      )}

      {/* 📊 System-Info Overlay */}
      {cameraActive && !dataScanned && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          background: 'rgba(0, 0, 0, 0.75)',
          padding: '12px 16px',
          borderRadius: '10px',
          fontSize: '13px',
          color: 'white',
          fontFamily: 'monospace',
          lineHeight: '1.9',
          backdropFilter: 'blur(4px)',
          zIndex: 9999,
          minWidth: '180px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
        }}>
          {/* Helligkeit */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span>💡 Licht:</span>
            <span style={{ 
              color: systemInfo.brightness >= 40 && systemInfo.brightness <= 80 ? '#4CAF50' : '#ff9800',
              fontWeight: 'bold'
            }}>
              {systemInfo.brightness}%
            </span>
          </div>

          {/* Batterie (nur wenn verfügbar) */}
          {systemInfo.battery && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>🔋 Akku:</span>
              <span style={{ 
                color: systemInfo.battery.level > 20 ? '#4CAF50' : '#f44336',
                fontWeight: 'bold'
              }}>
                {systemInfo.battery.level}% {systemInfo.battery.charging ? '⚡' : ''}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QuestVideoCapture;
